// SafeHtml — thin wrapper that always runs DOMPurify on content before
// rendering with dangerouslySetInnerHTML. This component is the ONLY
// sanctioned HTML-inject point in the codebase; a project-level ESLint
// rule bans raw `dangerouslySetInnerHTML` everywhere else (see
// /app/frontend/.eslintrc.security.js).
//
// SECURITY AUDIT NOTE — the `dangerouslySetInnerHTML` call below is intentional
// and review-approved. The `__html` value is the output of `DOMPurify.sanitize`
// with the conservative allowlist below (plain markdown tags + anchor hrefs).
// Any flag from a generic "dangerouslySetInnerHTML" scanner hitting this file
// is a false positive — SafeHtml exists precisely to centralize sanitization.
//
// Prefer this over inline `dangerouslySetInnerHTML={{ __html: ... }}` for
// anything that contains user-authored, LLM-authored, or markdown-rendered
// content. Never pass already-trusted server HTML through here; use plain
// React children instead.

import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';

const DEFAULT_CONFIG = {
  // Conservative allowlist — plenty for markdown -> HTML rendering.
  ALLOWED_TAGS: [
    'b', 'strong', 'i', 'em', 'u', 'code', 'pre',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'p', 'br', 'hr',
    'ul', 'ol', 'li',
    'a', 'span', 'div',
  ],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
  ALLOW_DATA_ATTR: false,
};

/**
 * @param {object} props
 * @param {string} props.html     Raw (potentially-unsafe) HTML string.
 * @param {string} [props.as='span']  Tag to wrap the sanitized output in.
 * @param {string} [props.className]
 * @param {object} [props.config] Custom DOMPurify config; merged over defaults.
 */
export const SafeHtml = ({ html, as: Tag = 'span', className, config }) => {
  const sanitized = useMemo(
    () => DOMPurify.sanitize(html || '', { ...DEFAULT_CONFIG, ...(config || {}) }),
    [html, config],
  );
  return (
    <Tag
      className={className}
      // eslint-disable-next-line react/no-danger -- sanitized above via DOMPurify
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
};

export default SafeHtml;
